import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import Breadcrumbs from '../components/Breadcrumbs';
import '../App.css';
import '../components/SearchResults.css';
import './DetailPage.css';

interface Jurisprudence {
    id: number;
    numero?: string;
    datearret?: string;
    juridiction?: string;
    chambre?: string;
    cour?: string;
    objet?: string;
    objetar?: string;
    text: string;
    has_translation?: boolean;
}

const JurisprudenceDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [jps, setJps] = useState<Jurisprudence | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchJurisprudence();
    }, [id, i18n.language]);

    const fetchJurisprudence = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/jurisprudence/${id}?lang=${i18n.language}`);
            setJps(response.data);
        } catch (err) {
            console.error('Error fetching jurisprudence:', err);
            setError('Erreur lors du chargement de la jurisprudence');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !jps) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">{error || 'Jurisprudence non trouvée'}</div>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left me-2"></i>Retour
                </button>
            </div>
        );
    }

    const displayContent = jps.text;
    const hasNoTranslation = jps.has_translation === false;
    const objet = i18n.language.startsWith('ar')
        ? (jps.objetar || jps.objet)
        : (jps.objet || jps.objetar);

    const breadcrumbs = [
        {
            label: t('search.types.jps'),
            onClick: () => navigate(-1)
        },
        { label: jps.juridiction || t('detail.jurisprudence') }
    ];

    return (
        <div className="detail-page">
            <Breadcrumbs items={breadcrumbs} />

            {/* Card 1: Attributes / Metadata */}
            <div className="result-card type-jps mb-4 animate-fade-in">
                <div className="result-card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <span className="premium-badge badge-jps mb-2 d-inline-block">
                                <i className="bi bi-bank me-2"></i>{t('search.types.jps')}
                            </span>
                            <h1 className="h3 fw-bold text-dark mb-2 result-title" style={{ fontSize: '1.75rem' }}>
                                {jps.juridiction || t('search.juridictionUnknown')}
                            </h1>
                            {jps.chambre && (
                                <h2 className="h5 text-muted mb-3">{jps.chambre}</h2>
                            )}
                            {objet && (
                                <p className="text-dark mb-3" style={{ fontWeight: 500 }}>
                                    {objet}
                                </p>
                            )}
                        </div>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left me-2"></i>{t('detail.back')}
                        </button>
                    </div>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="row g-4">
                        {jps.datearret && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-danger me-3">
                                        <i className="bi bi-calendar3 fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.date')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{new Date(jps.datearret).toLocaleDateString()}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {jps.numero && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-danger me-3">
                                        <i className="bi bi-hash fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.number')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{jps.numero}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {jps.cour && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-danger me-3">
                                        <i className="bi bi-geo-alt-fill fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.court')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{jps.cour}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Card 2: Content */}
            <div className="result-card type-jps animate-fade-in">
                <div className="result-card-body p-4">

                    {/* No Translation Warning with link to other language */}
                    {hasNoTranslation && (
                        <div className="alert alert-warning d-flex align-items-center justify-content-between mb-4">
                            <span>
                                <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
                                {t('detail.noTranslation')}
                            </span>
                            <Link
                                to={`/${i18n.language === 'ar' ? 'fr' : 'ar'}/detail/jurisprudence/${id}`}
                                className="btn btn-warning"
                            >
                                {i18n.language === 'ar' ? 'Voir en français' : 'عرض بالعربية'}
                            </Link>
                        </div>
                    )}

                    {/* Content Section */}
                    {displayContent && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-4 border-start border-4 border-primary ps-3">
                                <h4 className="fw-bold mb-0 text-dark">
                                    {t('detail.fullText')}
                                </h4>
                            </div>

                            <div
                                className="content-text"
                                style={{ fontSize: '1.1rem', lineHeight: '1.8', fontFamily: 'Georgia, serif' }}
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayContent) }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JurisprudenceDetail;
